import {
    ChangeDetectionStrategy,
    Component,
    computed,
    Injector,
    Input,
    OnInit,
    signal,
    Signal,
    ViewChild,
    ViewEncapsulation,
    WritableSignal,
} from '@angular/core';
import { IPlandayRoleMatch } from '@fi-app/external-platform-integration/planday/planday';
import { ILightWeightObject } from '@fi-shared/models';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { PlandayService } from '@fi-app/external-platform-integration/planday/planday.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';

enum PlandayRoleGroupType {
    PrevMatched,
    NotMatched,
    ExcludedFromImport,
}

interface IPlandayMatchROlesCardData {
    type: PlandayRoleGroupType;
    items: Signal<IPlandayRoleMatch[]>;
}

@Component({
    selector: 'planday-roles-matching',
    templateUrl: './planday-roles-matching.component.html',
    styleUrls: ['./planday-roles-matching.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'planday-roles-matching' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PlandayRolesMatchingComponent,
        },
    ],
    standalone: false,
})
export class PlandayRolesMatchingComponent
    extends ControlValueAccessorValidatorBase<IPlandayRoleMatch[]>
    implements OnInit
{
    @Input() set plandayRoleMatches(plandayRoleMatches: IPlandayRoleMatch[]) {
        this._plandayRoleMatches.set(plandayRoleMatches);
    }
    @Input() roles: ILightWeightObject[];
    @ViewChild(FiValidatingFormDirective, { static: true }) plandayRolesMatchingForm: FiValidatingFormDirective;

    private _plandayRoleMatches: WritableSignal<IPlandayRoleMatch[]> = signal([]);
    private excludedFromImportPlandayGroups: Signal<IPlandayRoleMatch[]> = computed(() => {
        return this._plandayRoleMatches().filter((roleMatch: IPlandayRoleMatch) => {
            return roleMatch.ExcludeFromImport;
        });
    });
    private prevMatchedPlandayGroups: Signal<IPlandayRoleMatch[]> = computed(() => {
        return this._plandayRoleMatches().filter((roleMatch: IPlandayRoleMatch) => {
            return !roleMatch.ExcludeFromImport && !!roleMatch.Id;
        });
    });
    private notMatchedPlandayGroups: Signal<IPlandayRoleMatch[]> = computed(() => {
        return this._plandayRoleMatches().filter((roleMatch: IPlandayRoleMatch) => {
            return !roleMatch.ExcludeFromImport && !roleMatch.Id;
        });
    });
    plandayEmployeeGroupNames: Signal<string[]> = computed(() => {
        return this._plandayRoleMatches().map((roleMatch: IPlandayRoleMatch) => roleMatch.EmployeeGroupName);
    });
    filterItemsName: WritableSignal<string[]> = signal([]);

    readonly plandayMatchRolesCardsData: IPlandayMatchROlesCardData[] = [
        {
            type: PlandayRoleGroupType.NotMatched,
            items: this.notMatchedPlandayGroups,
        },
        {
            type: PlandayRoleGroupType.PrevMatched,
            items: this.prevMatchedPlandayGroups,
        },
        {
            type: PlandayRoleGroupType.ExcludedFromImport,
            items: this.excludedFromImportPlandayGroups,
        },
    ];

    private readonly PlandayRoleGroupTypeLabels: { [key in PlandayRoleGroupType]: string } = {
        [PlandayRoleGroupType.NotMatched]: 'pk.matching_user_not_matched',
        [PlandayRoleGroupType.PrevMatched]: 'pk.matching_user_prev_matched',
        [PlandayRoleGroupType.ExcludedFromImport]: 'planday.exclude_from_import',
    } as const;

    isLocalRoleOptionDisabledFn: FiDropdownItemDisabledFn<ILightWeightObject> = (
        item: ILightWeightObject,
        allItems: ILightWeightObject[],
    ) => {
        return !!this._plandayRoleMatches().find((plandayRoleMatch: IPlandayRoleMatch) => {
            return plandayRoleMatch.RoleId === item.Id;
        });
    };

    constructor(
        private injector: Injector,
        private plandayService: PlandayService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiCustomerDataService: FiCustomerDataService,
        private appTextService: AppTextService,
        private fiSnackbarService: FiSnackBarService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.saveChangesWarningService.start(() => this._plandayRoleMatches());
    }

    importEmployeeGroups() {
        this.plandayService.getEmployeeGroupMatches().then((roleMatches: IPlandayRoleMatch[]) => {
            this._plandayRoleMatches.set(roleMatches);
            setTimeout(() => this.plandayRolesMatchingForm.updateValidity());
        });
    }

    onRolesMatchingChanged() {
        this.plandayRolesMatchingForm.updateValidity();
    }

    onExcludeFromImportChanged(roleMatch: IPlandayRoleMatch) {
        if (roleMatch.ExcludeFromImport) {
            roleMatch.RoleId = undefined;
        }
        this.onRolesMatchingChanged();
    }

    isSaveButtonDisabled(): boolean {
        return (
            !this.saveChangesWarningService.hasChanges() ||
            !this._plandayRoleMatches().length ||
            !!this.plandayRolesMatchingForm.errors
        );
    }

    savePlandayRoleMatches() {
        this.saveChangesWarningService.stop();
        this.plandayService.savePlandayRoleMatches(this._plandayRoleMatches()).then(() => {
            this.filterItemsName.set([]);
            this._plandayRoleMatches.update((plandayRolesMatches: IPlandayRoleMatch[]) => [...plandayRolesMatches]);
            this.saveChangesWarningService.start(() => this._plandayRoleMatches());
            this.fiSnackbarService.showSnackbar({
                textMessage: this.appTextService.get('planday.role_match_save_snackbar_message'),
                buttonText: this.appTextService.get('common.dismiss'),
            });
        });
    }

    getPlandayMatchRoleGroupsHeadingByType(roleGroupType: PlandayRoleGroupType, itemsCount: number): string {
        const isPlural: boolean = itemsCount > 1;
        return (
            this.appTextService.get(this.PlandayRoleGroupTypeLabels[roleGroupType]) +
            ' - ' +
            itemsCount +
            ' ' +
            this.appTextService.get(isPlural ? 'planday.employee_groups' : 'planday.employee_group')
        );
    }

    isRoleNotMatched(roleGroupType: PlandayRoleGroupType): boolean {
        return roleGroupType === PlandayRoleGroupType.NotMatched;
    }

    isRolePrevMatched(roleGroupType: PlandayRoleGroupType): boolean {
        return roleGroupType === PlandayRoleGroupType.PrevMatched;
    }

    getCustomerDisplayName(): string {
        return this.fiCustomerDataService.customerData.DisplayName;
    }

    filterRoleMatches(roleMatch: IPlandayRoleMatch): boolean {
        if (!this.filterItemsName().length) return true;

        return this.filterItemsName().some((name: string) => {
            return roleMatch.EmployeeGroupName.toLowerCase().includes(name.toLowerCase());
        });
    }

    isRoleGroupCardHidden(roleGroupData: IPlandayMatchROlesCardData): boolean {
        return !roleGroupData.items().some((item: IPlandayRoleMatch) => this.filterRoleMatches(item));
    }
}
